import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { useGSAP } from "@gsap/react";
import Header from "./components/common/Header.jsx";
import Footer from "./components/common/Footer.jsx";
import { SmootherContext } from "./components/common/SmootherContext.js";
import ReactDOM from "react-dom";
import Close from "./assets/Close.jsx";
import Logo from "./assets/Logo.jsx";
import ArrowTopRight from "./assets/arrowTopRight.jsx";
import { loadOneTrust } from "./utilities/oneTrust.js";
import { loadGoogleAnalytics } from "./utilities/googleAnalytics.js";
import SEOHeadTags from "./components/common/SEOHeadTags.jsx";

gsap.registerPlugin(useGSAP, ScrollTrigger, ScrollSmoother);

function App() {
  const [searchVisible, setSearchVisible] = useState(false);
  const searchFormRef = useRef();

  const handleFormSubmit = () => {
    if (searchFormRef.current) {
      searchFormRef.current.submit(); // Submit the form programmatically
    }
  };

  // keyboard navigation to close the lightbox
  useEffect(() => {
      const handleKeyDown = (event) => {
          if (event.key === 'Escape' || event.key === 'Esc') {
              closeLightbox();
          }
      };

      if (searchVisible) {
          document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
          document.removeEventListener('keydown', handleKeyDown);
      };
  }, [searchVisible]);

  useEffect(() => {
    loadGoogleAnalytics();
  }, []);

  useEffect(() => {
    loadOneTrust();
  }, []);

  const smootherRef = useRef(null);

  // based on the routes we need to setup
  const location = useLocation();
  const path = location.pathname;
  const lightHeaderPaths = [
    "/en/studios",
    "/ko/studios",
    "/en/projects",
    "/ko/projects",
    "/en/about",
    "/ko/about",
    "/en/labs",
    "/ko/labs",
  ];

  const darkHeaderOverrides = [
    "/en/studios/alternative-formats",
    "/ko/studios/alternative-formats",
  ];

  const lightHeader =
    lightHeaderPaths.some(
      (prefix) =>
        path === prefix ||
        path === "/en" ||
        path == "/ko" ||
        path.startsWith(prefix + "/")
    ) && !darkHeaderOverrides.includes(path);

  useGSAP(() => {
    smootherRef.current = ScrollSmoother.create({
      smooth: 1,
      effects: true,
      smoothTouch: 0,
    });
  }, []);

  // Search lightbox
  const searchLightboxEl = useRef();
  const searchInputEl = useRef();

  const openLightbox = () => {
    setSearchVisible(true);

    // Wait for the DOM to update before focusing
    setTimeout(() => {
      searchInputEl.current?.focus();
    }, 0);
  };

  const closeLightbox = () => {
    setSearchVisible(false);
  };

  function SearchLightBox() {
    const { lang } = useParams();

    return ReactDOM.createPortal(
      <div id="lightbox" className={`lightbox search ${searchVisible ? "active" : ""}`} ref={searchLightboxEl}>
        <div className="header">
          <div className="logo">
            <Logo />
          </div>
          <div className="close" onClick={() => closeLightbox()}>
            <Close /> {lang === "ko" ? "닫다" : "CLOSE"}
          </div>
        </div>
        <div className="body">
          {lang === "ko" ? (
            <h2>
              이것은 파이프라인이 아닙니다.
              <br />
              놀이터예요.
            </h2>
          ) : (
            <h2>
              This isn't a pipeline.
              <br />
              It's a playground.
            </h2>
          )}

          <form method="get" action={`/${lang}/search`} ref={searchFormRef}>
            <input
              type="text"
              name="keyword"
              id="keyword"
              placeholder={lang === "ko" ? "찾다" : "Search"}
              ref={searchInputEl}
              required
            />
            <svg
              id="searchIcon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z"
                stroke="var(--global-foreground)"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="submit_icon" onClick={handleFormSubmit} role="button" tabIndex={0} onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleFormSubmit();
            }}>
              <ArrowTopRight color="#181818" />
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <>
      <SEOHeadTags />

      <Header isLight={lightHeader} openSearch={openLightbox} />
      <SmootherContext.Provider value={smootherRef.current}>
        <div id="smooth-wrapper">
          <div id="smooth-content">
            <main>
              <Outlet />
            </main>
            <Footer />
          </div>
        </div>
      </SmootherContext.Provider>
      <SearchLightBox />
    </>
  );
}

export default App;
